import { Component, effect, HostListener, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { AuthStore } from '@/store/auth.store';
import { UserStore } from '@/store/user.store';
import { HeaderComponent } from '@/components/header/header.component';
import { FooterComponent } from '@/components/footer/footer.component';
import { SnackbarComponent } from '@/components/snackbar/snackbar.component';
import { SidebarNavComponent } from '@/components/sidebar-nav/sidebar-nav.component';
import { BlogSidebarNavComponent } from '@/components/blog-sidebar-nav/blog-sidebar-nav.component';
import { BreakpointService } from '@/services/breakpoint/breakpoint.service';
import { SidebarUiStore } from '@/store/sidebar-ui.store';
import { APP_ROUTES } from '@/routes/routes.type';

@Component({
  selector: 'app-container',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SnackbarComponent,
    SidebarNavComponent,
    BlogSidebarNavComponent,
  ],
  templateUrl: './container.component.html',
  styleUrl: './container.component.css',
  standalone: true,
})
export class ContainerComponent {
  readonly #router = inject(Router);
  userStore = inject(UserStore);
  authStore = inject(AuthStore);
  readonly breakpointService = inject(BreakpointService);
  readonly sidebarUiStore = inject(SidebarUiStore);

  readonly isBlogContext = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.#isBlogUrl()),
      startWith(this.#isBlogUrl()),
    ),
    { initialValue: this.#isBlogUrl() },
  );

  #isBlogUrl(): boolean {
    const path = this.#router.url.split('?')[0];
    return path === `/${APP_ROUTES.BLOG}` || path.startsWith(`/${APP_ROUTES.BLOG}/`);
  }

  constructor() {
    let wasAuthenticated = false;
    effect(() => {
      const isAuthenticated = this.authStore.isAuthenticated();
      const shouldLoadUser = isAuthenticated && !wasAuthenticated;
      wasAuthenticated = isAuthenticated;

      // Резервная загрузка: только при переходе false→true (login), если user ещё не загружен
      if (shouldLoadUser && !this.userStore.user()) {
        this.userStore.loadCurrentUser().catch(() => {
          /* guard handles redirect */
        });
      }
    });

    effect(() => {
      if (this.breakpointService.deviceType() === 'desktop') {
        this.sidebarUiStore.close();
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.breakpointService.deviceType() !== 'desktop' && this.sidebarUiStore.isOpen()) {
      this.sidebarUiStore.close();
    }
  }
}
